/**
 * TextEncryptionService
 *
 * Flutter-byte-identical text encryption for chat messages. Derives an
 * AES-GCM 256 key via ECDH P-256 between the local user's private JWK
 * (stored in SecureStorage at `SECURE_STORAGE_KEYS.PRIVATE_JWK`) and the
 * peer user's `publicJwk` (read from the peer's user document).
 *
 * Wire format (COORDINATED cross-platform change — Task 16):
 *  - IV is a FRESH random 12-byte nonce generated PER MESSAGE via
 *    `crypto.getRandomValues` and PREPENDED to the ciphertext — the SAME
 *    random-IV-prepended convention as the binary path below (identical
 *    12-byte length and `[ iv ‖ ciphertext ]` order, so both share ONE
 *    convention). This REPLACES the previous fixed 21-byte ASCII IV
 *    `'Initialization Vector'` and is a breaking change on a LIVE
 *    cross-platform feature: it must ship in LOCKSTEP with the matching
 *    Flutter `e2ee_impl.dart` change, and old fixed-IV text history will fail
 *    to decrypt (accepted — surfaces as the `'Could not decrypt message'`
 *    literal). There is NO discriminator/feature flag (consideration A).
 *  - Ciphertext output is one UTF-16 code unit per byte over the
 *    `[ iv ‖ ciphertext ]` byte stream (matches Flutter's
 *    `String.fromCharCodes(...)`); the Firestore storage TYPE is unchanged
 *    (still a string) — only the bytes now lead with the per-message IV.
 *  - Decryption input is consumed via the same one-code-unit-per-byte
 *    decoding (matches Flutter's `message.codeUnits` read path); the leading
 *    12-byte IV is split off and used to decrypt the remainder.
 *  - On any initialization failure, derived key is left null and subsequent
 *    `encrypt`/`decrypt` calls return the input unchanged (Flutter parity:
 *    `lib/presentation/utils/e2ee_impl.dart:47-50, 65-67, 92-94`).
 *  - On encrypt failure, returns literal `'Could not encrypt message'`
 *    (Flutter: `e2ee_impl.dart:86`).
 *  - On decrypt failure, returns literal `'Could not decrypt message'`
 *    (Flutter: `e2ee_impl.dart:109`).
 *
 * Flutter source of truth: `lib/presentation/utils/e2ee_impl.dart` (full file)
 * — the random-per-message-IV-prepended format must be mirrored there in the
 * same coordinated release; the failure literals above are unchanged.
 *
 * Architecture: UseCase → TextEncryptionService → SecureStorage + SubtleCrypto.
 * No UI, hook, Redux, or page wiring in this branch — service is available
 * for the future chat feature and verified by unit tests.
 *
 * No `crypto-js` fallback: `crypto-js@4` does not implement ECDH (no ECC
 * primitives — only AES/DES/RC4/Rabbit symmetric ciphers plus SHA/MD5/HMAC
 * hashing and PBKDF2/EvpKDF KDFs) or AES-GCM (only CBC/CFB/CTR/ECB/OFB modes
 * ship). The WebCrypto/CryptoJS factory pattern used by
 * `videoEncryptionService` does not apply here because video encryption is
 * AES-128-CBC, which `crypto-js` supports; ECDH P-256 + AES-GCM has no
 * `crypto-js` counterpart. Old browsers without `crypto.subtle` fall through
 * the existing null-key pass-through path documented at `initialize()`,
 * matching Flutter (`e2ee_impl.dart:47-50`). If a non-WebCrypto fallback ever
 * becomes necessary, adopt `@noble/ciphers` (AES-GCM) + `@noble/curves`
 * (ECDH P-256) in a dedicated branch and introduce the interface/factory
 * split at that point.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class TextEncryptionService {
  /**
   * Length (bytes) of the random per-message / per-file IV prepended to
   * ciphertext. 12 bytes is the AES-GCM-recommended nonce length (NIST
   * SP 800-38D). SHARED by BOTH the text path (encrypt/decrypt) and the binary
   * path (encryptBinary/decryptBinary) so the two use ONE convention — same
   * length AND same `[ iv ‖ ciphertext ]` prepend order. This replaces the
   * old fixed 21-byte ASCII text IV `'Initialization Vector'` (Task 16,
   * coordinated cross-platform change).
   */
  private static readonly IV_LENGTH = 12;

  /**
   * Derived AES-GCM 256 key. Null when initialize() failed, the peer public
   * JWK was missing, or the local private JWK was missing — in which case
   * encrypt/decrypt pass the input through unchanged (Flutter parity).
   */
  private aesGcmKey: CryptoKey | null = null;

  /**
   * Fingerprint (`${x}.${y}` of the peer's public EC coordinates) of the peer
   * the current `aesGcmKey` was derived for. Lets a repeat `initialize` with the
   * SAME peer short-circuit instead of re-reading SecureStorage and re-running
   * ECDH `deriveBits` + `importKey` — the open-chat self-heal path
   * (`EncryptedCachedImage.fetchAndDecrypt`) calls `initialize` before every
   * distinct-`src` decrypt, so without this guard a conversation with N media
   * tiles triggers N identical key derivations where 1 suffices.
   *
   * Set on successful derivation; reset to null on every null-key path
   * (subtle unavailable, null peer, missing private JWK, derivation failure) so
   * a later same-peer call after a failure still re-derives.
   */
  private currentPeerFingerprint: string | null = null;

  /**
   * Initialize the derived AES-GCM key from the local user's private JWK
   * (read from SecureStorage) and the peer user's public JWK. If
   * `peerPublicJwk` is null/undefined, leaves the derived key unset and
   * subsequent encrypt/decrypt return the input unchanged.
   *
   * Short-circuits when a key is already derived for the same peer (matching
   * `currentPeerFingerprint`), making repeated same-peer calls a no-op rather
   * than a fresh SecureStorage read + ECDH derive.
   *
   * Flutter parity: `lib/presentation/utils/e2ee_impl.dart:45-61`.
   */
  async initialize(peerPublicJwk: JsonWebKey | null | undefined): Promise<void> {
    // Same-peer short-circuit: if we already hold a key derived for this exact
    // peer, skip the SecureStorage decrypt + ECDH derive entirely. The
    // fingerprint is null for a null/undefined peer, so this never matches the
    // null-key pass-through path below.
    const fingerprint = peerPublicJwk != null ? `${peerPublicJwk.x}.${peerPublicJwk.y}` : null;
    if (this.aesGcmKey !== null && fingerprint !== null && fingerprint === this.currentPeerFingerprint) {
      return;
    }

    // Defensive: non-secure-context or very old browser. Production browsers
    // all expose `crypto.subtle` under HTTPS, so this branch is purely a
    // safety net to keep the documented "encrypt/decrypt never throw"
    // guarantee explicit instead of relying on the catch in the try below
    // (which would log a misleading 'Failed to derive E2EE key').
    if (typeof crypto === 'undefined' || !crypto.subtle) {
      appLogger.error('crypto.subtle unavailable', 'TextEncryptionService.initialize');
      this.aesGcmKey = null;
      this.currentPeerFingerprint = null;
      return;
    }

    // messages will not be encrypted (Flutter: e2ee_impl.dart:47-50)
    if (peerPublicJwk == null) {
      this.aesGcmKey = null;
      this.currentPeerFingerprint = null;
      return;
    }

    try {
      const privateJwk = await SecureStorageWebCrypto.getInstance().getEncrypted<JsonWebKey>(
        SECURE_STORAGE_KEYS.PRIVATE_JWK,
      );

      if (privateJwk == null) {
        appLogger.error('Private JWK not in SecureStorage', 'TextEncryptionService.initialize');
        this.aesGcmKey = null;
        this.currentPeerFingerprint = null;
        return;
      }

      const ecdhPublicKey = await crypto.subtle.importKey(
        'jwk',
        peerPublicJwk,
        { name: 'ECDH', namedCurve: 'P-256' },
        false,
        [],
      );

      const ecdhPrivateKey = await crypto.subtle.importKey(
        'jwk',
        privateJwk,
        { name: 'ECDH', namedCurve: 'P-256' },
        false,
        ['deriveBits'],
      );

      const derivedBits = await crypto.subtle.deriveBits(
        { name: 'ECDH', public: ecdhPublicKey },
        ecdhPrivateKey,
        256,
      );

      this.aesGcmKey = await crypto.subtle.importKey(
        'raw',
        derivedBits,
        { name: 'AES-GCM' },
        false,
        ['encrypt', 'decrypt'],
      );
      // Record the peer this key was derived for so a repeat same-peer
      // initialize short-circuits above.
      this.currentPeerFingerprint = fingerprint;
    } catch (error) {
      // Flutter parity: e2ee_impl.dart:58-60 — on any failure, aesGcmSecretKey
      // is set to null and the service falls through to pass-through mode.
      this.aesGcmKey = null;
      this.currentPeerFingerprint = null;
      appLogger.error('Failed to derive E2EE key', 'TextEncryptionService.initialize', {
        error: error as Error,
      });
    }
  }

  /**
   * Encrypt a UTF-8 text message. Generates a FRESH random 12-byte IV per
   * message, AES-GCM-encrypts, and emits `[ iv ‖ ciphertext ]` as one UTF-16
   * code unit per byte (matches Flutter's `String.fromCharCodes(...)`). Same
   * random-IV-prepended convention as `encryptBinary` (one shared format).
   *
   * Two encrypts of the same plaintext now differ (random IV). The Firestore
   * storage TYPE is unchanged (still a string) — only the bytes now lead with
   * the per-message IV.
   *
   * COORDINATED cross-platform change (Task 16): must ship in lockstep with the
   * matching Flutter `e2ee_impl.dart` change. Flutter parity (failure literal +
   * null-key pass-through): `lib/presentation/utils/e2ee_impl.dart:63-88`.
   */
  async encrypt(message: string): Promise<string> {
    if (this.aesGcmKey === null) {
      return message;
    }

    try {
      const iv = crypto.getRandomValues(new Uint8Array(TextEncryptionService.IV_LENGTH));
      const data = new TextEncoder().encode(message);
      const cipherBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, this.aesGcmKey, data);

      // Prepend the IV: [ iv ‖ ciphertext ], then emit one UTF-16 code unit per
      // byte — Flutter byte-for-byte equivalent of `String.fromCharCodes(...)`.
      const cipherBytes = new Uint8Array(cipherBuf);
      const out = new Uint8Array(iv.length + cipherBytes.length);
      out.set(iv, 0);
      out.set(cipherBytes, iv.length);
      return String.fromCharCode(...out);
    } catch (error) {
      appLogger.error('encrypt failed', 'TextEncryptionService.encrypt', {
        error: error as Error,
      });
      // Literal string MUST match Flutter (e2ee_impl.dart:86). Do not translate.
      return 'Could not encrypt message';
    }
  }

  /**
   * Decrypt a ciphertext-as-string produced by `encrypt` (or the coordinated
   * Flutter counterpart). Consumes one UTF-16 code unit per byte — matches
   * Flutter's `message.codeUnits` read path — then splits the leading 12-byte
   * IV off and AES-GCM-decrypts the remainder with it (same convention as
   * `decryptBinary`).
   *
   * COORDINATED cross-platform change (Task 16): old FIXED-IV text history was
   * stored WITHOUT a leading per-message IV, so it no longer round-trips — its
   * first 12 bytes get consumed as a (wrong) IV and AES-GCM's auth-tag check
   * fails, surfacing as the `'Could not decrypt message'` literal. This is
   * accepted (no discriminator/feature flag — consideration A).
   *
   * Flutter parity (failure literal + null-key pass-through):
   * `lib/presentation/utils/e2ee_impl.dart:90-111`.
   */
  async decrypt(message: string): Promise<string> {
    if (this.aesGcmKey === null) {
      return message;
    }

    try {
      const bytes = Uint8Array.from(message, (c) => c.charCodeAt(0));
      const iv = bytes.subarray(0, TextEncryptionService.IV_LENGTH);
      const cipherBytes = bytes.subarray(TextEncryptionService.IV_LENGTH);
      const plainBuf = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        this.aesGcmKey,
        cipherBytes,
      );
      return new TextDecoder().decode(plainBuf);
    } catch (error) {
      appLogger.error('decrypt failed', 'TextEncryptionService.decrypt', {
        error: error as Error,
      });
      // Literal string MUST match Flutter (e2ee_impl.dart:109). Do not translate.
      return 'Could not decrypt message';
    }
  }

  /**
   * Encrypt arbitrary binary data (chat image / thumbnail bytes) with the same
   * ECDH-derived AES-GCM key the text path uses. Web-first media-crypto design
   * (no Flutter parity — consideration E).
   *
   * Wire format — same `[ iv ‖ ciphertext ]` byte convention as the text path
   * (Task 16 aligned both onto one shared 12-byte random-IV-prepended format):
   *  - A FRESH random 12-byte IV is generated per call via
   *    `crypto.getRandomValues` and PREPENDED to the ciphertext. A reused IV
   *    across many payloads under one key breaks AES-GCM, so every call gets a
   *    fresh nonce.
   *  - Output ArrayBuffer layout: `[ iv (12 bytes) ‖ ciphertext ]`. (The text
   *    path emits the identical byte layout, just over the one-code-unit-per-
   *    byte string surface Firestore stores.)
   *
   * Null-key contract (mirrors the text path's pass-through): when the derived
   * key is null — never-initialized, peer had no `publicJwk`, or derivation
   * failed — the input ArrayBuffer is returned UNCHANGED. The text path's
   * literal `'Could not encrypt message'` sentinel is text-only and is NOT
   * applicable here.
   *
   * Failure contract: on a SubtleCrypto failure the error is logged via
   * `appLogger.error` and RETHROWN — there is no in-band sentinel for binary
   * (a magic byte stream would be indistinguishable from real ciphertext), so
   * the caller's error path (placeholder render) handles it.
   */
  async encryptBinary(data: ArrayBuffer): Promise<ArrayBuffer> {
    if (this.aesGcmKey === null) {
      return data;
    }

    try {
      const iv = crypto.getRandomValues(new Uint8Array(TextEncryptionService.IV_LENGTH));
      const cipherBuf = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        this.aesGcmKey,
        data,
      );

      // Prepend the IV: [ iv ‖ ciphertext ].
      const cipherBytes = new Uint8Array(cipherBuf);
      const out = new Uint8Array(iv.length + cipherBytes.length);
      out.set(iv, 0);
      out.set(cipherBytes, iv.length);
      // `Uint8Array.buffer` is typed `ArrayBufferLike` under the ES2020 lib;
      // `out` was freshly allocated as an exact-fit ArrayBuffer, so the cast is
      // safe (codebase convention — webCryptoVideoEncryptionService.ts:108).
      return out.buffer as ArrayBuffer;
    } catch (error) {
      appLogger.error('encryptBinary failed', 'TextEncryptionService.encryptBinary', {
        error: error as Error,
      });
      // No in-band sentinel for binary — rethrow so the caller's error path
      // handles it (consideration E).
      throw error;
    }
  }

  /**
   * Decrypt binary data produced by `encryptBinary`. Splits the leading 12-byte
   * IV off the buffer and AES-GCM-decrypts the remainder with it, returning the
   * plaintext ArrayBuffer. Web-first media-crypto design (no Flutter parity —
   * consideration E).
   *
   * Null-key contract (mirrors the text path's pass-through): when the derived
   * key is null the input ArrayBuffer is returned UNCHANGED.
   *
   * Failure contract: a non-conforming / legacy payload (not produced by
   * `encryptBinary` — wrong IV, truncated, or unencrypted Flutter-origin
   * media) fails AES-GCM's auth-tag check inside `crypto.subtle.decrypt`. The
   * error is logged and RETHROWN so the caller renders the error/placeholder
   * (consideration A/E: legacy media is accepted to fail to decrypt). There is
   * no text-path-style literal sentinel for binary.
   */
  async decryptBinary(data: ArrayBuffer): Promise<ArrayBuffer> {
    if (this.aesGcmKey === null) {
      return data;
    }

    try {
      const bytes = new Uint8Array(data);
      const iv = bytes.subarray(0, TextEncryptionService.IV_LENGTH);
      const cipherBytes = bytes.subarray(TextEncryptionService.IV_LENGTH);
      return await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, this.aesGcmKey, cipherBytes);
    } catch (error) {
      appLogger.error('decryptBinary failed', 'TextEncryptionService.decryptBinary', {
        error: error as Error,
      });
      // No in-band sentinel for binary — rethrow so the caller's error path
      // handles legacy/non-conforming payloads (consideration A/E).
      throw error;
    }
  }
}

// Singleton instance — pattern matches webCryptoVideoEncryptionService:324.
// Not registered globally or wired into any hook/page in this branch.
export const textEncryptionService = new TextEncryptionService();
